class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      const upsellButton = event.target.closest('.nk-cart-upsell__add');
      if (upsellButton) this.addUpsell(upsellButton);
    });
    this.quickAddHandler = this.handleQuickAdd.bind(this);
    document.addEventListener('submit', this.quickAddHandler);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.updateNooklineCartCount(event?.cartData);
      });
    }
    this.setHeaderCartIconAccessibility();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this.quickAddHandler);
    this.cartUpdateUnsubscriber?.();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = [
      document.querySelector('#cart-icon-bubble'),
      ...document.querySelectorAll('.site-header .cart-icon-wrap, .mobile-tabbar a[href]'),
    ].filter((link, index, links) => {
      if (!link || links.indexOf(link) !== index) return false;
      if (link.id === 'cart-icon-bubble' || link.classList.contains('cart-icon-wrap')) return true;
      try {
        return new URL(link.href, window.location.origin).pathname === routes.cart_url;
      } catch (_error) {
        return false;
      }
    });

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.classList.remove('is-just-added');
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    window.setTimeout(() => this.classList.remove('is-just-added'), 250);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.add('is-just-added');
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionHTML = parsedState.sections?.[section.id];
      if (!sectionHTML) return;
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
    });
    this.updateNooklineCartCount(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  async handleQuickAdd(event) {
    const form = event.target.closest('.nk-quick-add-form');
    if (!form || form.dataset.nkSubmitting === 'true') return;

    event.preventDefault();
    const button = form.querySelector('[name="add"]');
    const label = button?.querySelector('[data-nk-quick-add-label]') || button;
    const status = form.querySelector('[data-nk-quick-add-status]');
    const originalLabel = label?.textContent;
    form.dataset.nkSubmitting = 'true';
    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('loading');
    if (label) label.textContent = 'Adding…';
    if (status) {
      status.hidden = true;
      status.textContent = '';
    }

    try {
      const formData = new FormData(form);
      formData.append(
        'sections',
        this.getSectionsToRender().map((section) => section.id),
      );
      formData.append('sections_url', window.location.pathname);
      this.setActiveElement(button);

      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');

      this.renderContents(data);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        // source 必须用 'cart-items'：renderContents 已经用 /cart/add.js 带回的 sections 渲染完抽屉，
        // 而 CartItems 的订阅对 'cart-items' 会直接短路。若换成自定义 source，它会再发一次
        // GET /cart?section_id=cart-drawer（整页级渲染，比 /cart/add.js 重得多）。
        // 又因为 pubsub 的 publish 返回 Promise.all(订阅者返回值)、而该订阅回调返回的是 fetch promise，
        // 这里的 await 会被那次多余请求拖住 —— 按钮会长时间停在 "Adding…"。改后端到端只剩一次请求。
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          productVariantId: formData.get('id'),
          cartData: data,
        });
      }
    } catch (error) {
      if (status) {
        status.textContent = error.message;
        status.hidden = false;
      }
    } finally {
      delete form.dataset.nkSubmitting;
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('loading');
      if (label && label.isConnected) label.textContent = originalLabel;
    }
  }

  async addUpsell(button) {
    if (button.disabled) return;
    const label = button.querySelector('span');
    const originalLabel = label?.textContent;
    const errorElement = this.querySelector('.nk-cart-upsell__error');
    button.disabled = true;
    button.classList.add('is-loading');
    if (label) label.textContent = 'Adding…';
    if (errorElement) errorElement.hidden = true;

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');
      this.setActiveElement(button);
      this.renderContents(data);
    } catch (error) {
      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
      if (label && label.isConnected) label.textContent = originalLabel;
    }
  }

  updateNooklineCartCount(cartData) {
    let itemCount = Number(cartData?.item_count);
    if (!Number.isFinite(itemCount)) {
      const bubbleHTML = cartData?.sections?.['cart-icon-bubble'];
      if (bubbleHTML) {
        const bubble = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.cart-count-bubble span[aria-hidden="true"]');
        itemCount = Number(bubble?.textContent?.trim());
      }
    }
    if (!Number.isFinite(itemCount)) return;

    document.querySelectorAll('.site-header .cart-icon-wrap, .mobile-tabbar a[href]').forEach((link) => {
      let isCartLink = link.classList.contains('cart-icon-wrap');
      if (!isCartLink) {
        try {
          isCartLink = new URL(link.href, window.location.origin).pathname === routes.cart_url;
        } catch (_error) {
          isCartLink = false;
        }
      }
      if (!isCartLink) return;

      let badge = link.querySelector('[data-nk-cart-count], .cart-count');
      if (!badge && itemCount > 0) {
        badge = document.createElement('span');
        badge.className = 'cart-count';
        badge.dataset.nkCartCount = '';
        link.appendChild(badge);
      }
      if (!badge) return;
      badge.textContent = itemCount > 99 ? '99+' : String(itemCount);
      badge.hidden = itemCount === 0;
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
